import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { sortEventOccurrences } from "../sort-event-occurrences/index.mjs";
import { useAdapterContext } from "../use-adapter-context/index.mjs";

/**
 * Places event occurrences for a timeline UI.
 */
export function useEventOccurrencesWithTimelinePosition(parameters) {
  const {
    occurrences,
    maxSpan
  } = parameters;
  const adapter = useAdapterContext();
  return React.useMemo(() => {
    const sortedOccurrences = sortEventOccurrences(occurrences);
    const conflicts = buildOccurrenceConflicts(adapter, sortedOccurrences);
    const {
      firstIndexLookup,
      maxIndex
    } = buildFirstIndexLookup(conflicts);
    const lastIndexLookup = buildLastIndexLookup(conflicts, firstIndexLookup, maxIndex, maxSpan);
    const occurrencesWithPosition = sortedOccurrences.map(occurrence => _extends({}, occurrence, {
      position: {
        firstIndex: firstIndexLookup[occurrence.key],
        lastIndex: lastIndexLookup[occurrence.key]
      }
    }));
    return {
      occurrences: occurrencesWithPosition,
      maxIndex
    };
  }, [adapter, occurrences, maxSpan]);
}
/**
 * Looks for conflicts between occurrences and build a list of conflicts for each occurrence.
 * The provided occurrences need to be sorted by starting date-time.
 */
function buildOccurrenceConflicts(adapter, occurrences) {
  const getEmptyBlock = () => ({
    occurrences: [],
    longestDurationMs: 0
  });
  const occurrencesBlocks = [];
  let currentBlock = getEmptyBlock();
  let lastEndTimestamp = 0;

  // Group occurrences in non-overlapping blocks to reduce the number of comparisons when looking for conflicts.
  // Computes the properties needed for each occurrence.
  for (const occurrence of occurrences) {
    const startTimestamp = occurrence.displayTimezone.start.timestamp;
    const endTimestamp = occurrence.displayTimezone.end.timestamp;
    const occurrenceDurationMs = endTimestamp - startTimestamp;
    if (startTimestamp >= lastEndTimestamp) {
      if (currentBlock.occurrences.length > 0) {
        occurrencesBlocks.push(currentBlock);
      }
      currentBlock = getEmptyBlock();
      lastEndTimestamp = 0;
    }
    currentBlock.occurrences.push({
      key: occurrence.key,
      startTimestamp,
      endTimestamp
    });
    if (occurrenceDurationMs > currentBlock.longestDurationMs) {
      currentBlock.longestDurationMs = occurrenceDurationMs;
    }
    if (endTimestamp > lastEndTimestamp) {
      lastEndTimestamp = endTimestamp;
    }
  }
  if (currentBlock.occurrences.length > 0) {
    occurrencesBlocks.push(currentBlock);
  }

  // For each block, looks for conflicts between occurrences to build the conflicts list.
  const conflicts = [];
  for (const block of occurrencesBlocks) {
    for (let i = 0; i < block.occurrences.length; i += 1) {
      const occurrence = block.occurrences[i];
      const conflictsBefore = new Set();
      const conflictsAfter = new Set();
      for (let j = i + 1; j < block.occurrences.length; j += 1) {
        const occurrenceA = block.occurrences[j];
        if (occurrenceA.startTimestamp < occurrence.endTimestamp) {
          conflictsAfter.add(occurrenceA.key);
        } else {
          // We know that all the next occurrences will start even later, so we can stop here.
          break;
        }
      }
      for (let j = i - 1; j >= 0; j -= 1) {
        const occurrenceB = block.occurrences[j];
        const diffBetweenOccurrencesStart = occurrence.startTimestamp - occurrenceB.startTimestamp;
        if (diffBetweenOccurrencesStart > block.longestDurationMs) {
          // We know that all the previous occurrences won't end after the start of the occurrence we are getting conflicts for, so we can stop here.
          break;
        }
        if (occurrenceB.endTimestamp > occurrence.startTimestamp) {
          conflictsBefore.add(occurrenceB.key);
        }
      }
      conflicts.push({
        key: occurrence.key,
        before: conflictsBefore,
        after: conflictsAfter
      });
    }
  }
  return conflicts;
}
function buildFirstIndexLookup(conflicts) {
  let maxIndex = 1;
  const firstIndexLookup = {};
  for (const occurrence of conflicts) {
    if (occurrence.before.size === 0) {
      firstIndexLookup[occurrence.key] = 1;
    } else {
      const usedIndexes = new Set(Array.from(occurrence.before).map(conflictingOccurrence => firstIndexLookup[conflictingOccurrence]));
      let i = 1;
      while (usedIndexes.has(i)) {
        i += 1;
      }
      firstIndexLookup[occurrence.key] = i;
      if (i > maxIndex) {
        maxIndex = i;
      }
    }
  }
  return {
    firstIndexLookup,
    maxIndex
  };
}
function buildLastIndexLookup(conflicts, firstIndexLookup, maxIndex, maxSpan) {
  if (maxSpan < 2) {
    return firstIndexLookup;
  }
  const lastIndexLookup = {};
  for (const occurrence of conflicts) {
    const usedIndexes = new Set([...Array.from(occurrence.before), ...Array.from(occurrence.after)].map(conflictingOccurrence => firstIndexLookup[conflictingOccurrence]));
    const firstIndex = firstIndexLookup[occurrence.key];
    let lastIndex = firstIndex;
    while (!usedIndexes.has(lastIndex + 1) && lastIndex < maxIndex && lastIndex - firstIndex < maxSpan - 1) {
      lastIndex += 1;
    }
    lastIndexLookup[occurrence.key] = lastIndex;
  }
  return lastIndexLookup;
}

/**
 * A block of occurrences that overlap in time.
 * The occurrences of two distinct blocks never overlap in time, their conflicts can thus be computed independently.
 */